import { Box, Center, Flex, HStack, Progress, Text, Tooltip } from '@chakra-ui/react'
import { MessageThread, UserGroup, WithId } from '@hb/shared'
import { useCallback, useContext, useMemo, useState } from 'react'

import { ProfilePicturePopover } from '../../Admins/ProfilePicturePopover'
import { PulsingCircle } from '../../Animation/PulsingCirlce'

import {
  PopUpMessageContext,
  useApp,
  useAuth,
  useMe,
  useThreadsList,
  useThreadTitle,
} from '@hb/react'
import { getBadgeColor } from '../../Users/UserBadge'
import { reportThreadRead } from '../utils'
import { RecentMessageView } from './Messages/RecentMessageView'
import './styles.css'

const ThreadTitleBadge = ({
  initials,
  accessLevel,
  title,
}: {
  accessLevel: UserGroup
  initials: string
  title: string
}) => {
  const { appName } = useApp()
  return (
    <Tooltip placement="top" hasArrow label={title}>
      <Center
        fontSize="sm"
        fontWeight={600}
        color="white"
        bg={getBadgeColor(appName, accessLevel)}
        position="relative"
        borderRadius="full"
        boxShadow="1px 1px 4px rgba(0,0,0,0.3)"
        width={10}
        whiteSpace="nowrap"
        height={10}>
        <Text lineHeight={1} textShadow="1px 1px 3px #00000099" fontFamily="Comfortaa">
          {initials}
        </Text>
        {/* {unread ? <ThreadUnreadIndicator /> : null} */}
      </Center>
    </Tooltip>
  )
}

const DirectMessageThreadBadge = ({ userId, size = 10 }: { userId: string; size?: number }) => (
  <Box position="relative">
    <ProfilePicturePopover noOnlineIndicator userId={userId} size={size} noPopover />
  </Box>
)

export const ThreadPreview = ({
  thread,
  isChild,
  onClick,
}: {
  thread: WithId<MessageThread>
  isChild?: boolean
  onClick?: () => void
}) => {
  const authUser = useAuth(s => s.authUser)
  const mostRecentMessage = thread.mostRecentMessages?.[0]
  const me = useMe()
  const uid = useMemo(() => authUser?.uid ?? null, [authUser])
  const unreadByMe = useMemo(() => thread.unreadBy.includes(me?.uid ?? ''), [thread, me])
  const {
    widget: { threadSelection },
  } = useThreadsList()
  const { title, initials, directMessageUserId } = useThreadTitle(
    uid,
    thread.type,
    thread.id,
    thread,
  )
  const { showMessage } = useContext(PopUpMessageContext)
  const { appName } = useApp()
  const [reportReadLoading, setReportReadLoading] = useState(false)
  const handleClick = useCallback(async () => {
    if (!onClick || !uid) return
    if (
      !threadSelection ||
      threadSelection.id !== thread.id ||
      threadSelection.type !== thread.type
    ) {
      onClick()
    } else if (unreadByMe) {
      setReportReadLoading(true)
      try {
        await reportThreadRead(appName, thread.type, thread.id)
      } catch (e: any) {
        showMessage({
          text: 'Failed to mark thread as read',
          type: 'error',
          subText: e.message,
        })
      }
      setReportReadLoading(false)
    }
  }, [threadSelection, unreadByMe, onClick, thread, appName, showMessage, uid])

  return (
    <Flex
      onClick={handleClick}
      w="100%"
      cursor={onClick ? 'pointer' : 'default'}
      className={unreadByMe ? 'thread-preview-unread' : 'thread-preview'}
      bg="white"
      boxShadow={isChild ? 'none' : '0 0 4px rgba(0,0,0,0.2)'}
      fontFamily="Hero-New"
      fontWeight={500}
      borderRadius={0}
      position="relative"
      height="auto"
      align="center"
      display="flex"
      px={2}
      py={1}
      flexFlow="row"
      gap={2}>
      {directMessageUserId ? (
        <DirectMessageThreadBadge userId={directMessageUserId} />
      ) : (
        <ThreadTitleBadge accessLevel={thread.accessLevel} title={title} initials={initials} />
      )}
      <Flex py={1} minH="30px" minW="0" flex={1} align="flex-start" flexDir="column">
        <HStack w="100%" spacing={1}>
          <Text maxW="100%" isTruncated color="gray.600">
            {title}
          </Text>
          {unreadByMe ? (
            <Box position="relative" h={4} w={8} ml="auto">
              <PulsingCircle style={{ height: '13px', width: '13px', right: '2px', top: '4px' }} />
            </Box>
          ) : null}
        </HStack>
        {mostRecentMessage ? (
          <RecentMessageView message={mostRecentMessage} />
        ) : (
          <Text lineHeight={1} pb={1} fontSize="sm" color="gray.500" fontStyle="italic">
            No messages yet
          </Text>
        )}
      </Flex>
      {reportReadLoading ? (
        <Progress
          colorScheme="green"
          w="100%"
          h="5px"
          position="absolute"
          left={0}
          bottom={0}
          isIndeterminate
        />
      ) : null}
    </Flex>
  )
}
